import { registerLocaleData } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de-BE';
import localeFr from '@angular/common/locales/fr-BE';
import localeNl from '@angular/common/locales/nl-BE';
import {
  APP_INITIALIZER,
  enableProdMode, ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  BrowserModule,
  bootstrapApplication,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, Router, withPreloading } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  MissingTranslationHandler,
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  ConfigurationModule,
  ConfigurationService,
} from '@smals/ngx-configuration-service';
import {
  VersionControlConfig,
  VersionControlModule,
  VersionControlRefreshMode,
} from '@smals/ngx-version-control';
import { BnNgIdleService } from 'bn-ng-idle';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgHttpLoaderModule } from 'ng-http-loader';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { APP_VERSION } from './app/app-version';
import { initializer } from './app/authentication/keycloakInit';
import { CustomDateAdapter } from './app/custom-date-adapter';
import { CONFIGURATION_DATA } from './app/env.config';
import { apiInterceptor } from './app/interceptors/api.interceptor';
import { RootComponent } from './app/root.component';
import { routes } from './app/routes';
import { UserService } from './app/services/user/user.service';
import { CustomMissingTranslationHandler } from './app/shared/handlers/missing-translation.handler';
import { PaginatorI18n } from './app/shared/modules/paginator.i18n';
import { effects } from './app/store/effects/app.effects';
import { appReducer } from './app/store/reducers/app.reducer';
import { environment } from './environments/environment';
import { LocalStorageService } from './app/services/local-storage/local-storage.service';
import { createErrorHandler, TraceService } from '@sentry/angular';

function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

function versionControlConfigFactory(): VersionControlConfig {
  return {
    version: APP_VERSION,
    refreshMode: VersionControlRefreshMode.SNOOZE,
    maxTime: 300000,
    maxRequests: 10,
  };
}

const APP_DATE_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'long' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

registerLocaleData(localeDe, 'de-BE');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeNl, 'nl-BE');
registerLocaleData(localeFr, 'fr-BE');

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(RootComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      MatNativeDateModule,
      MatSnackBarModule,
      MatDialogModule,
      NgHttpLoaderModule.forRoot(),
      ConfigurationModule.forRoot(CONFIGURATION_DATA),
      VersionControlModule.forRoot(versionControlConfigFactory),
      KeycloakAngularModule,
      StoreModule.forRoot(appReducer),
      EffectsModule.forRoot(effects),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: CustomMissingTranslationHandler,
        },
      }),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: initializer,
      multi: true,
      deps: [KeycloakService, ConfigurationService, UserService, LocalStorageService, TraceService],
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS,
    },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorI18n
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false
      })
    },
    {
      provide: TraceService,
      deps: [Router]
    },
    BnNgIdleService,
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideEnvironmentNgxMask(),
    provideHttpClient(withInterceptors([apiInterceptor])),
    provideAnimations(),
  ],
}).catch(err => console.error(err));
